import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, EMPTY } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

import { catchError, tap } from 'rxjs/operators';
import { GlobalService } from './global.service';
import { environment } from '../../environments/environment';
import { LocalCacheService } from './localCache.service';
import { ConfigService, KvResponse } from './config.service';
import { ActivatedRoute } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class PDSService {
  configSetting: KvResponse;
  httpOptions: any;
  lastestPDS: any;
  currentLatestTMD: any;
  partnerCode: any;
  private partnercodeSource = new BehaviorSubject<string>('');
  currentPartnercode = this.partnercodeSource.asObservable();

  constructor(
    private http: HttpClient,
    private globalService: GlobalService,
    private localCacheService: LocalCacheService,
    private configService: ConfigService,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.configSetting = this.configService.getConfig() ?? '';
    this.partnerCode = this.getWhiteLabelPartnerCode() ?? '';

    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    this.getTMD();
    let requestUrl = `${environment.baseAPIUrl}quote/document/pds/latest?club=${environment.autoClub}`;
    if (this.partnerCode === 'Klook' || this.partnerCode == 'vacatio') {
      requestUrl = `${environment.baseAPIUrl}quote/document/pds/latest?club=${environment.autoClub}&partnerCode=${this.partnerCode}`;
    }
    if (!isPlatformBrowser(this.platformId)) return;
    this.http.get<any>(requestUrl, this.httpOptions).subscribe({
      next: (response: any) => {
        this.lastestPDS = response.pds[0];
      },
      error: (error) => {
        console.error('Error fetching latest PDS', error);
      },
    });
  }

  getAllPDS() {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    this.globalService.showLoading('getAllPDS');
    let requestUrl = `${environment.baseAPIUrl}quote/document/pds?club=${environment.autoClub}`;
    if (this.partnerCode === 'Klook' || this.partnerCode == 'vacatio') {
      requestUrl = `${environment.baseAPIUrl}quote/document/pds?club=${environment.autoClub}&partnerCode=${this.partnerCode}`;
    }
    return this.http.get<any>(requestUrl, this.httpOptions).pipe(
      tap((_) => {
        this.globalService.hideLoading('getAllPDS');
        console.log('fetched all pds');
      }),
      catchError(this.globalService.handleError<any>('all pds', [])),
    );
  }

  getLatestPDS() {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    this.globalService.showLoading('getLatestPDS');
    let requestUrl = `${environment.baseAPIUrl}quote/document/pds/latest?club=${environment.autoClub}`;
    if (this.partnerCode === 'Klook' || this.partnerCode == 'vacatio') {
      requestUrl = `${environment.baseAPIUrl}quote/document/pds/latest?club=${environment.autoClub}&partnerCode=${this.partnerCode}`;
    }
    return this.http.get<any>(requestUrl, this.httpOptions).pipe(
      tap((_) => {
        this.globalService.hideLoading('getLatestPDS');
        console.log('fetched latest pds');
      }),
      catchError(this.globalService.handleError<any>('latest pds', [])),
    );
  }

  downloadPDSPDF(url: string, fileName: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const newWindow = window.open(url, '_blank');
    if (newWindow) {
      newWindow.opener = null;
    } else {
      // Handle the case where the new tab could not be opened
      alert(
        'Unable to open PDF in a new tab. Please check your browser settings. Make sure Block Pop-ups is turned off or/and Make sure JavaScript is turned on',
      );
    }
  }

  downloadLatestPDSPDF(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.lastestPDS) {
      alert('No PDS available');
      return;
    }
    const newWindow = window.open(this.lastestPDS.url, '_blank');
    if (newWindow) {
      newWindow.opener = null;
    } else {
      // Handle the case where the new tab could not be opened
      alert(
        'Unable to open PDF in a new tab. Please check your browser settings. Make sure Block Pop-ups is turned off or/and Make sure JavaScript is turned on',
      );
    }
  }

   getAllTMD() {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    this.globalService.showLoading('getAllTMD');
    let requestUrl = `${environment.baseAPIUrl}quote/document/tmd?club=${environment.autoClub}`;
    if (this.partnerCode === 'Klook' || this.partnerCode == 'vacatio') {
      requestUrl = `${environment.baseAPIUrl}quote/document/tmd?club=${environment.autoClub}&partnerCode=${this.partnerCode}`;
    }
    return this.http.get<any>(requestUrl, this.httpOptions).pipe(
      tap((_) => {
        this.globalService.hideLoading('getAllTMD');
        console.log('fetched all tmd');
      }),
      catchError(this.globalService.handleError<any>('all tmd', [])),
    );
  }

  getTMD() {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    this.currentLatestTMD =
      this.localCacheService.getSessionStorage('lastestTMD');
    let requestUrl = `${environment.baseAPIUrl}quote/document/tmd?club=${environment.autoClub}`;
    if (!this.currentLatestTMD) {
      this.http.get<any>(requestUrl, this.httpOptions).subscribe({
        next: (response: any) => {
          if (response && response.tmd && response.tmd.length > 0) {
            response.tmd.forEach((obj: any) => {
              if (obj.isActive) {
                this.localCacheService.saveSessionStorage('lastestTMD', obj);
              }
            });
          }
        },
        error: (error) => {
          console.error('Error fetching latest TMD', error);
        },
      });
    }
    return EMPTY;
  }

  getLatestTMD() {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    let requestUrl = `${environment.baseAPIUrl}quote/document/tmd?club=${environment.autoClub}`;
    return this.http.get<any>(requestUrl, this.httpOptions).pipe(
      tap((_) => {
        console.log('fetched latest tmd');
      }),
      catchError(this.globalService.handleError<any>('latest tmd', [])),
    );
  }

  isEmpty(obj: any) {
    if (!isPlatformBrowser(this.platformId)) return true;
    return Object.keys(obj).length === 0;
  }

  getWhiteLabelPartnerCode() {
    if (!isPlatformBrowser(this.platformId)) return '';
    let whiteLabelPartnerCode = '';
    const vendorDetails =
      this.localCacheService.getSessionStorage('vendorDetail');
    whiteLabelPartnerCode = vendorDetails?.partnerCode ?? '';
    this.route.queryParams.subscribe((params) => {
      if (!this.isEmpty(params)) {
        const newParams = Object.fromEntries(
          Object.entries(params).map(([k, v]) => [k.toLowerCase(), v]),
        );
        if (
          newParams['partnercode'] === 'Klook' ||
          newParams['partnercode'] === 'vacatio'
        ) {
          whiteLabelPartnerCode = newParams['partnercode'];
          const promo = newParams['promo'];
          const vendorDetailObject = {
            partnerCode: newParams['partnercode'],
            promoCode: promo,
            vname: newParams['partnercode'],
          };

          this.localCacheService.saveSessionStorage(
            'vendorDetail',
            vendorDetailObject,
          );
        }
      }
    });
    return whiteLabelPartnerCode;
  }

  changePartnercode(code: string) {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    this.partnercodeSource.next(code);
    this.partnerCode = code;
    if (code === 'Klook' || code === 'vacatio') {
      let requestUrl = `${environment.baseAPIUrl}quote/document/pds/latest?club=${environment.autoClub}&partnerCode=${code}`;
      this.http.get<any>(requestUrl, this.httpOptions).subscribe({
        next: (response: any) => {
          this.lastestPDS = response.pds[0];
        },
        error: (error) => {
          console.error('Error fetching latest PDS', error);
        },
      });
    }
    return EMPTY;
  }

  getWhiteLabelQueryParams(partnerCode: string) {
    if (partnerCode === 'Klook') {
      return { partnerCode: 'Klook' };
    } else if (partnerCode === 'vacatio') {
      return { partnerCode: 'vacatio' };
    } else {
      return {};
    }
  }

  getWhiteLabelPdsHref(partnerCode: string) {
    if (partnerCode === 'Klook') {
      return '/whitelabelpds?partnerCode=Klook';
    } else if (partnerCode === 'vacatio') {
      return '/whitelabelpds?partnerCode=vacatio';
    } else {
      return '/policydocumentation';
    }
  }

  getWhiteLabelHref(baseUrl: string, partnerCode: string) {
    if (partnerCode === 'Klook' || partnerCode === 'vacatio') {
      return `${baseUrl}?partnerCode=${partnerCode}&vname=${partnerCode}`;
    }
    return baseUrl;
  }

  getWhiteLabelPdsRouterLink(partnerCode: string) {
    if (partnerCode === 'Klook' || partnerCode === 'vacatio') {
      return '/whitelabelpds';
    }
    return '/policydocumentation';
  }
}
